<template>
  <v-app :class="[style.disableShadows ? 'disable-shadow' : '']" :key="key">
    <template v-if="globalError">
      <error-page />
    </template>
    <template v-else>
      <style-manager-menu v-if="isUserReseller" reseller />
      <style-manager-menu v-else-if="isAdminLoggedIn" />

      <navbar v-if="$route.meta.displayNavbar && readyToRender" />
      <new-instance-floating-button
        v-if="$route.meta.displayNavbar && $vuetify.breakpoint.mobile"
      />
      <v-main
        v-if="readyToRender"
        app
        :class="$route.meta.displayNavbar ? '' : ''"
      >
        <TrialInfoBox v-if="$route.meta.displayNavbar" />
        <alert-manager />
        <router-view />

        <demo-send-feedback-button
          v-if="$store.state.demoMode || showFeedbackButton"
        />

        <demo-footer
          v-if="$store.state.demoMode && $route.meta.displayNavbar"
        />

        <div
          v-else-if="$route.meta.displayNavbar && !$store.state.demoMode"
          class="mt-10 w-100"
        ></div>
      </v-main>
      <onboarding-modal-wrapper ref="onboardingModalWrapper" />

      <TrialExpiredModal ref="trialExpiredModal" />

      <Footer
        v-if="$store.state.home && $store.state.home.user"
        @languageChanged="refreshApp"
      />
    </template>
  </v-app>
</template>


<script>
// import DOMPurify from "dompurify";
import AlertManager from "./components/alerts/AlertManager.vue";
import NewInstanceFloatingButton from "./components/buttons/NewInstanceFloatingButton.vue";
import Navbar from "./components/navbar/Navbar.vue";
import Footer from "./components/footer/Footer.vue";
import DemoFooter from "./components/footer/DemoFooter.vue";
import StyleManagerMenu from "./components/styleManager/StyleManagerMenu.vue";
import OnboardingModalWrapper from "./components/onboarding/OnboardingModalWrapper.vue";
import TrialInfoBox from "./components/navbar/TrialInfoBox.vue";
import TrialExpiredModal from "./components/modal/TrialExpiredModal.vue";

import CustomThemeMixin from "./mixins/CustomThemeMixin.js";

import Api from "@/apis/Api";

import ErrorPage from "./views/ErrorPage.vue";
import DemoSendFeedbackButton from "./components/DemoSendFeedbackButton.vue";
import moment from "moment";

export default {
  components: {
    Navbar,
    AlertManager,
    NewInstanceFloatingButton,
    ErrorPage,
    DemoFooter,
    DemoSendFeedbackButton,
    StyleManagerMenu,
    OnboardingModalWrapper,
    Footer,
    TrialInfoBox,
    TrialExpiredModal,
  },
  mixins: [CustomThemeMixin],
  name: "App",
  data() {
    return {
      readyToRender: true,
      globalError: false,
      key: 0,
    };
  },
  beforeCreate() {
    document.body.classList.add(
      this.$store.state.ThemeModule.style?.darkMode ? "is-dark" : "is-light"
    );
  },
  computed: {
    isAppSingleServer() {
      return !!this.$store.state.home.app.single_server?.active;
    },
    showFeedbackButton() {
      if (!this.isAppSingleServer) {
        return false;
      }
      return !!this.$store.state.home.app.single_server?.show_feedback_button;
    },
    isUserReseller() {
      return (
        this.$store.state.home &&
        this.$store.state.home.user &&
        "reseller_settings" in this.$store.state.home.user &&
        this.$store.state.home.user.reseller_settings !== null
      );
    },
    isAdminLoggedIn() {
      return "admin" in this.$store.state.home;
    },
    customCss() {
      return this.$store.state.home.app.custom_css;
    },
    customJsHeader() {
      return this.$store.state.home.app.custom_js_header;
    },
    customJsFooter() {
      return this.$store.state.home.app.custom_js_footer;
    },
  },
  mounted() {
    this.$vuetify.theme.dark =
      this.$store.state.home.app.theme?.style?.darkMode;
    if (this.customJsHeader) {
      this.appendScript(this.customJsHeader, document.head);
    }
    if (this.customCss) {
      this.appendStyle(this.customCss, document.head);
    }
    if (this.customJsFooter) {
      this.appendScript(this.customJsFooter, document.body);
    }
    if (this.hasExpiredTrial()) {
      this.$refs.trialExpiredModal.open();
    }
  },
  created() {
    this.$root.$on("after-login", () => {
      this.redirect = localStorage.getItem(
        window.location.origin + "-redirect"
      );
      if (
        this.redirect == "new-site" &&
        !this.$store.state.services.hasLimitReached()
      ) {
        this.$root.$emit("start-installation-process");
        this.$refs.onboardingModalWrapper.creationTypeSelected(0, {
          source: "after-login",
        });
      } else if (
        this.redirect == "new-import" &&
        !this.$store.state.services.hasLimitReached()
      ) {
        this.$root.$emit("start-installation-process");
        this.$refs.onboardingModalWrapper.creationTypeSelected(1);
      } else if (
        this.redirect == "control-panel-upgrade" &&
        !this.$store.state.services.hasLimitReached()
      ) {
        this.$root.$emit("start-installation-process");
        localStorage.removeItem(window.location.origin + "-token");
        this.$refs.onboardingModalWrapper.startInstanceSynchronizationProcess();
      }
      localStorage.removeItem(window.location.origin + "-redirect");
    });
    this.$root.$on("app-ready-to-render", (ready) => {
      this.readyToRender = ready;
    });
    Api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Show alert when forbidden error code is received
        if (error.response.status === 403) {
          // this.$store.dispatch("addAlert", {
          //   success: false,
          //   errorMessage: "Action not available in demo mode.",
          // });
          if (process.env.VUE_APP_DEMO_MODE) {
            error.response.data.message = "Action not available in demo mode.";
          }
        }
        // if (error.response.status === 429) {
        //   this.globalError = true;
        // }
        return Promise.reject(error);
      }
    );
  },
  methods: {
    refreshApp() {
      this.key++;
    },
    removeScriptTags(content) {
      return content.replace(
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
        ""
      );
    },
    appendScript(content, parentElement) {
      const script = document.createElement("script");
      script.textContent = content;
      parentElement.appendChild(script);
    },
    appendStyle(content, parentElement) {
      const styleBlock = `<style type="text/css">${content}</style>`;
      const divFragmentCss = document
        .createRange()
        .createContextualFragment(styleBlock);
      parentElement.append(divFragmentCss);
    },
    hasExpiredTrial() {
      if (!this.$store.state.services.data) {
        return false;
      }
      let trialService = this.$store.state.services.getTrialService();
      if (!trialService) {
        return false;
      }
      if (!moment(trialService.trial_expiry_at).isBefore(moment())) {
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss">
.v-main__wrap {
  justify-content: unset !important; //?
}
</style>